import * as React from 'react';
import { List, CellMeasurer, CellMeasurerCache, ListRowProps } from 'react-virtualized';
import { style } from 'typestyle';
import textWidth from 'text-width';

import innerText from 'react-innertext';
import { FONT_FAMILY } from '../Inputs/InputSuggest/InputSuggest.styles';
import { useRef, useState } from 'react';

export interface VirtualizedListboxProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Optional props to control width&height if needed
   */
  listWidth?: number;
  /**
   * The maximum height for the listbox.
   * If the total estimated height is less than this, the listbox will shrink to fit its content.
   * Otherwise, it will be capped at this height and scroll.
   */
  maxListHeight?: number;
  /**
   * Whether to automatically highlight the first option when opened
   */
  autoHighlight?: boolean;
  /**
   * Index of the currently highlighted item
   */
  highlightedIndex?: number;
  isMultiSelect?: boolean;
}
const HORIZONTAL_PADDING = 32; // Total horizontal padding (left + right) for each item
const VERTICAL_PADDING = 12; // Total vertical padding (top + bottom) for each item
const LINE_HEIGHT = 24; // Height of each line of text
const SCROLLBAR_WIDTH = 10; // width of scrollbar
/**
 * Create a CellMeasurerCache allowing variable row heights.
 * The fallback row height is 36, but it will expand if text wraps.
 */
const cache = new CellMeasurerCache({
  defaultHeight: 36,
  fixedWidth: true,
});
const listWrapper = style({
  $nest: {
    '& .ReactVirtualized__Grid__innerScrollContainer': {
      maxWidth: 'none !important',
    },
    '.s5-MuiAutocomplete-option div': {
      width: '100%',
      overflowWrap: 'break-word',
      letterSpacing: 'normal', // this MUST be `normal` because the text-width calcs below rely on it to be
    },
  },
});

const VirtualizedListbox = React.forwardRef<HTMLUListElement, VirtualizedListboxProps>(
  function VirtualizedListbox(props, forwardedRef) {
    const {
      listWidth = 200,
      maxListHeight = 400,
      children,
      style: customStyle,
      autoHighlight = true,
      highlightedIndex = -1,
      isMultiSelect = false,
      ...other
    } = props;
    // Turn children into an array of items.
    const items = React.Children.toArray(children);
    const itemCount = items.length;

    // Estimate the total height by summing up (or approximating) each row’s height.
    // Since the cache might not have measured all rows yet, we use the default for any unknown.
    let totalEstimatedHeight = 0;
    for (let i = 0; i < itemCount; i++) {
      // Try to get the measured row height; if missing, fallback to cache.defaultHeight.
      const rowHeight = cache.rowHeight({ index: i }) || cache.defaultHeight;
      totalEstimatedHeight += rowHeight;
    }
    // The List's height will be the smaller of the total estimated height and the maxListHeight.
    const computedHeight = Math.min(totalEstimatedHeight, maxListHeight);

    const containerStyle: React.CSSProperties = {
      ...customStyle,
      overflow: 'hidden', //let list handle scrolling
    };

    // A ref to the List so we can force it to recompute heights when children change.
    const listRef = React.useRef<List>(null);
    const [domHighlightedIndex, setDomHighlightedIndex] = useState<number>(-1);
    const listboxRef = useRef<HTMLUListElement | null>(null);
    const setRefs = React.useCallback(
      (node: HTMLUListElement | null) => {
        listboxRef.current = node;
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else if (forwardedRef) {
          (forwardedRef as React.MutableRefObject<HTMLUListElement | null>).current = node;
        }
      },
      [forwardedRef],
    );

    // Whenever items change, clear the cache and re-measure.
    React.useEffect(() => {
      cache.clearAll();
      listRef.current?.recomputeRowHeights();
    }, [children, itemCount]);

    React.useEffect(() => {
      const node = listboxRef.current;
      if (!node) return;

      const updateFromDom = () => {
        const target = node.querySelector('[data-focus=\"true\"]') as HTMLElement | null;

        if (!target) {
          const itemLength = items.length;
          if (domHighlightedIndex > 0 && domHighlightedIndex < itemLength) {
            listRef.current?.scrollToRow(0);
          } else if (domHighlightedIndex === 0) {
            listRef.current?.scrollToRow(itemLength - 1);
          }
        } else {
          const attr = target.getAttribute('data-option-index');
          const idx = attr ? parseInt(attr, 10) : -1;
          if (!Number.isNaN(idx) && idx >= 0 && idx !== domHighlightedIndex) {
            setDomHighlightedIndex(idx);
            listRef.current?.scrollToRow(idx);
          }
        }
      };

      updateFromDom(); // initial sync

      const observer = new MutationObserver(updateFromDom);
      observer.observe(node, {
        subtree: true,
        attributes: true,
        attributeFilter: ['data-focus', 'aria-selected'],
      });
      return () => observer.disconnect();
    }, [domHighlightedIndex, highlightedIndex, items.length]);

    React.useEffect(() => {
      if (highlightedIndex != null && highlightedIndex >= 0) {
        listRef.current?.scrollToRow(highlightedIndex);
      }
    }, [highlightedIndex]);

    // Row renderer: wrap each row in a CellMeasurer so its height can adjust.
    function rowRenderer({ index, key, style, parent }: ListRowProps) {
      return (
        <CellMeasurer key={key} cache={cache} parent={parent} columnIndex={0} rowIndex={index}>
          {() => {
            // Check if this is the highlighted item
            const isHighlighted = isMultiSelect ? false : autoHighlight && index === highlightedIndex;

            // Merge provided row styles with any custom styles.
            const finalRowStyle: React.CSSProperties = {
              ...style,
              whiteSpace: 'normal',
              boxSizing: 'border-box',
              listStyle: 'none',
              backgroundColor: isHighlighted ? 'rgba(0, 168, 155, 0.45)' : undefined,
            };
            // Set aria-selected for accessibility
            return (
              <div style={finalRowStyle} aria-selected={isHighlighted}>
                {items[index]}
              </div>
            );
          }}
        </CellMeasurer>
      );
    }
    // caculate number of lines needed for wrapped text
    const getWrappedLines = (text: string, maxWidth: number): number => {
      // this regex is meant to match the behavior of overflowWrap: 'break-word'
      // however, 'break-word' will also break on any character once it reaches max width,
      // which is covered bby the code below
      const words = text.split(/\s+|(?<=-)/);
      let line = '';
      let lines = 1;
      // this will calculate width each word and see if it fits in the current line
      // if it does, add it to the current line else start a new line
      for (const word of words) {
        const currentWord = line ? line + ' ' + word : word;
        if (textWidth(currentWord, { family: FONT_FAMILY, size: 16, weight: '400' }) <= maxWidth) {
          line = currentWord;
        } else {
          lines += 1;
          // The code below here tries to mimic the behavior of 'break-word' when it breaks
          // on any character once it reaches max width
          // The options passed into `textWidth` must match what's in the dom exactly,
          // or else the calcualted width won't match and the line counts will end up wrong.
          // Also, `letterSpacing` must be `normal` or else they'll be wrong.
          // This width calcualtion is very delicate.

          let segment = '';
          for (const char of word) {
            const test = segment + char;
            const width = textWidth(test, { family: FONT_FAMILY, size: 16, weight: '400' });
            if (width <= maxWidth) {
              segment = test;
            } else {
              lines += 1;
              segment = char;
            }
          }
          line = segment;
        }
      }
      return lines;
    };
    function calculateRowHeight({ index }: { index: number }): number {
      const itemText = innerText(items[index] as React.ReactNode);
      const usableWidth = listWidth - HORIZONTAL_PADDING - (itemCount <= 2 ? 0 : SCROLLBAR_WIDTH);
      // calculate number of lines needed
      const lines = getWrappedLines(itemText, usableWidth);
      return VERTICAL_PADDING + lines * LINE_HEIGHT;
    }
    return (
      <ul ref={setRefs} {...other} style={containerStyle}>
        <List
          ref={listRef}
          autoWidth
          width={listWidth}
          height={computedHeight}
          rowCount={itemCount}
          deferredMeasurementCache={cache}
          rowHeight={calculateRowHeight}
          rowRenderer={rowRenderer}
          overscanCount={5}
          style={{ outline: 'none' }}
          className={listWrapper}
          role="listbox"
        />
      </ul>
    );
  },
);

export default VirtualizedListbox;
